import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, ThemePalette } from '@angular/material/core';
import { FloatLabelType, MatError, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PayerAliasItem } from '../../../models/payer-alias-item';
import { PayerConfigService } from '../../../services/payer/payer-config.service';

// New-look replacement for app-payer-alias-select. Matches aliases by ID; select-all covers active aliases
// (unassigned or this payer's), including ones hidden by search.
@Component({
  selector: 'app-payer-alias-filter-select',
  templateUrl: './payer-alias-filter-select.component.html',
  styleUrls: ['./payer-alias-filter-select.component.scss'],
  imports: [MatFormField, MatLabel, MatError, MatSelect, MatSelectTrigger, MatOption, MatTooltip, ReactiveFormsModule, NgxMatSelectSearchModule]
})
export class PayerAliasFilterSelectComponent implements OnInit {

  private readonly configService = inject(PayerConfigService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() form: FormGroup;
  @Input() controlName = 'payer-alias';
  @Input() color: ThemePalette = 'primary';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() multiple = true;
  @Input() label = 'Alias';
  @Input() placeholder = '';
  @Input() toolTipPosition: TooltipPosition = 'above';
  @Input() required = false;
  @Input() queryOptions = false;
  // Also list aliases tied to a payer (disabled unless they're the current payer's).
  @Input() showDisabledOptions = false;
  @Input() currentPayerID = 0;

  @Output() ready = new EventEmitter<void>();
  @Output() valueChanged = new EventEmitter<PayerAliasItem[]>();

  options: PayerAliasItem[] = [];
  filteredOptions: PayerAliasItem[] = [];
  readonly control = new FormControl<PayerAliasItem[] | null>(null);
  readonly filterControl = new FormControl<string>('', { nonNullable: true });

  readonly compareById = (a: PayerAliasItem, b: PayerAliasItem) => a?.ID === b?.ID;

  ngOnInit(): void {
    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    this.filterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applySearch());

    if (this.queryOptions) {
      this.getOptionData();
    }
  }

  getOptionData() {
    this.configService.getOrgPayerAliases().subscribe(result => {
      this.options = this.showDisabledOptions ? (result ?? []) : (result ?? []).filter(i => !i.PayerID);
      this.applySearch();
      this.ready.emit();
    });
  }

  // null clears, [] checks every active alias, otherwise selects by ID.
  setSelectedValues(values: PayerAliasItem[] | null) {
    if (!values) {
      this.clear();
      return;
    }

    if (values.length == 0) {
      this.allOptionClicked(true);
      return;
    }

    const ids = new Set(values.map(v => v.ID));
    this.setSelection(this.options.filter(o => ids.has(o.ID)));
  }

  // null clears, [] checks every active alias, otherwise selects by name.
  setSelectedValuesByName(names: string[] | null) {
    if (!names) {
      this.clear();
      return;
    }

    if (names.length == 0) {
      this.allOptionClicked(true);
      return;
    }

    this.setSelection(names.map(name => this.options.find(o => o.Name == name)).filter(o => !!o));
  }

  // [{ID: 0}] = every active alias.
  getValues(): any[] {
    if (this.allToggled()) {
      return [{ ID: 0 }];
    }

    return [...(this.control.value ?? [])];
  }

  getAliasIDsForPayer(): number[] {
    return (this.control.value ?? []).map(a => a.ID);
  }

  getAliasNamesForPayer(): string[] {
    return (this.control.value ?? []).map(a => a.Name);
  }

  // The select-all checkbox: a user change, so the control is marked dirty.
  onToggleAll(isChecked: boolean) {
    this.allOptionClicked(isChecked);
    this.control.markAsDirty();
  }

  // Aliases tied to another payer keep their state.
  allOptionClicked(isChecked: boolean) {
    const active = this.options.filter(o => this.isActive(o));
    const others = (this.control.value ?? []).filter(v => !active.some(a => a.ID === v.ID));
    this.setSelection(isChecked ? [...others, ...active] : others);
  }

  allToggled(): boolean {
    const counts = this.activeCounts();
    return this.multiple && counts.selected > 0 && counts.selected === counts.active;
  }

  indeterminate(): boolean {
    const counts = this.activeCounts();
    return this.multiple && counts.selected > 0 && counts.selected < counts.active;
  }

  isActive(option: PayerAliasItem): boolean {
    return !option.PayerID || option.PayerID == this.currentPayerID;
  }

  isOtherPayers(option: PayerAliasItem): boolean {
    return option.PayerID > 0 && option.PayerID != this.currentPayerID;
  }

  isCurrentPayers(option: PayerAliasItem): boolean {
    return this.currentPayerID > 0 && option.PayerID == this.currentPayerID;
  }

  getOptionTooltip(option: PayerAliasItem): string {
    return option.PayerID > 0 ? `Associated to ${option.PayerName}` : '';
  }

  summary(): string {
    const selected = this.control.value ?? [];

    if (selected.length <= 1) {
      return selected[0]?.Name ?? '';
    }

    return this.allToggled() ? 'All' : `${selected[0].Name} +${selected.length - 1}`;
  }

  tooltip(): string {
    const selected = this.control.value ?? [];
    return selected.length > 1 ? selected.map(o => o.Name).join(', ') : '';
  }

  onSelectionChange() {
    this.syncSelectedFlags();
    this.valueChanged.emit(this.control.value);
  }

  private setSelection(items: PayerAliasItem[]) {
    this.control.setValue(items);
    this.syncSelectedFlags();
  }

  private clear() {
    this.control.reset();
    this.control.setValue([]);
    this.filterControl.setValue('');
    this.syncSelectedFlags();
  }

  // Keeps option.Selected in step with the control.
  private syncSelectedFlags() {
    const ids = new Set((this.control.value ?? []).map(v => v.ID));
    this.options.forEach(o => o.Selected = ids.has(o.ID));
  }

  private activeCounts(): { active: number; selected: number } {
    const ids = new Set((this.control.value ?? []).map(v => v.ID));
    const active = this.options.filter(o => this.isActive(o));
    return { active: active.length, selected: active.filter(o => ids.has(o.ID)).length };
  }

  private applySearch() {
    const search = this.filterControl.value.trim().toLowerCase();
    this.filteredOptions = search
      ? this.options.filter(o => (o.Name ?? '').toLowerCase().includes(search))
      : this.options.slice();
  }
}
